import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { CTASection } from "@/components/CTASection";
import { RelatedServicesSection } from "@/components/RelatedServicesSection";
import { Sun, Zap, Home, Award, TrendingUp, Shield } from "lucide-react";
import { SEO } from "@/components/seo/SEO";
import { ServiceSchema } from "@/components/seo/ServiceSchema";
import { FAQSchema } from "@/components/seo/FAQSchema";

const Zonnepanelen = () => {
  const faqData = [
    {
      question: "Wat kosten zonnepanelen in 2025?",
      answer: "Zonnepanelen kosten gemiddeld tussen €4.000-€8.000 voor een compleet systeem, afhankelijk van het aantal panelen en uw dakoppervlak. Bij SOL-VI kunt u al vanaf €89 per maand beginnen met zonnepanelen."
    },
    {
      question: "Hoe snel verdienen zonnepanelen zich terug?",
      answer: "De terugverdientijd van zonnepanelen ligt tussen 5-8 jaar. Met de huidige energieprijzen en onze hoogrendement panelen vaak zelfs sneller. Na terugverdientijd levert uw systeem nog 15+ jaar gratis stroom."
    },
    {
      question: "Hoeveel energie bespaar ik met zonnepanelen?",
      answer: "Met zonnepanelen bespaart u gemiddeld 60-80% op uw elektriciteitsrekening. Een gemiddeld huishouden bespaart €800-€1.200 per jaar op energiekosten."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <SEO
          title="Zonnepanelen Kosten 2025 | Installatie & Rendement | SOL-VI Elst"
          description="⚡ Zonnepanelen installatie vanaf €4.000 🏆 25 jaar garantie ☀️ Tot 70% energiebesparing 📈 Terugverdientijd 5-8 jaar 🆓 Gratis advies & offerte Gelderland"
          keywords="zonnepanelen kosten 2025, zonnepanelen installatie Elst, zonnepanelen Gelderland, zonnepanelen rendement, PV panelen installatie, omvormer zonnepanelen, saldering 2025, zonnepanelen subsidie"
          canonical="https://sol-vi.nl/zonnepanelen"
          type="website"
          preload={[
            { href: "/lovable-uploads/256302e4-63c2-4966-8f66-00b153e5f5c7.png", as: "image", type: "image/png" }
          ]}
        />
        <ServiceSchema
          name="Zonnepanelen Installatie"
          description="Professionele installatie van hoogrendement zonnepanelen met 25 jaar garantie. Bespaar tot 70% op uw energierekening."
          provider="SOL-VI"
          areaServed={["Elst", "Arnhem", "Nijmegen", "Gelderland", "Betuwe"]}
          serviceType="Solar Panel Installation"
          url="https://sol-vi.nl/zonnepanelen"
          offers={[
            { name: "Basis Zonnepanelen Pakket", description: "10 panelen inclusief installatie", priceRange: "€4000-€6000" },
            { name: "Premium Zonnepanelen Pakket", description: "15+ panelen met optimizers", priceRange: "€6000-€9000" }
          ]}
        />
        <FAQSchema faqs={faqData} />
        {/* Hero Section */}
        <div className="pt-20 pb-12 bg-gradient-subtle">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Zonnepanelen Kosten 2025: Bespaar Tot 70% op Energie in Gelderland
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                Hoogrendement zonnepanelen installatie in Elst en omgeving. Snelle terugverdientijd van 5-8 jaar. Wek uw eigen stroom op en word energieneutraal.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="hero" size="lg" onClick={() => (window as any).renderOpenSolarWidget()}>
                  Vraag Gratis Offerte
                </Button>
                <Button variant="outline" size="lg" onClick={() => (window as any).Calendly.initPopupWidget({url: 'https://calendly.com/sol-vi/new-meeting?primary_color=ff7800', pageSettings: {backgroundColor: 'ffffff', hideEventTypeDetails: false, hideLandingPageDetails: false, primaryColor: 'ff7800', textColor: '4d5055'}, prefill: {}, utm: {}})}>
                  Plan Adviesgesprek
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              
              {/* Introduction */}
              <div className="mb-12">
                <p className="text-lg leading-relaxed text-muted-foreground">
                  Stelt u zich eens voor: uw dak wekt elke dag schone, gratis energie voor u op. Met de hoogwaardige zonnepanelen van SOL-VI is dit geen toekomstmuziek, maar realiteit. U verlaagt uw energierekening drastisch, verhoogt de waarde van uw woning en draagt direct bij aan een duurzamere wereld. Een slimme investering die zich snel terugverdient.
                </p>
              </div>

              {/* How it works */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold mb-6">Hoe werken zonnepanelen?</h2>
                <p className="text-lg leading-relaxed text-muted-foreground">
                  Zonnepanelen, ook wel PV-panelen genoemd, bestaan uit zonnecellen die zonlicht omzetten in gelijkstroom. Een omvormer in uw huis zet deze gelijkstroom vervolgens om in bruikbare wisselstroom voor al uw apparaten. Eventuele overtollige stroom die u opwekt, wordt teruggeleverd aan het net, waardoor uw energiemeter letterlijk terugloopt (salderen).
                </p>
              </div>

              {/* Benefits */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold mb-8">De voordelen van SOL-VI zonnepanelen:</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Forse besparing</h3>
                      <p className="text-muted-foreground">Bespaar tot wel 70% op uw jaarlijkse energiekosten.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Shield className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">25 Jaar garantie</h3>
                      <p className="text-muted-foreground">Wij installeren uitsluitend A-merk panelen met een lange product- en vermogensgarantie.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Zap className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Snelle terugverdientijd</h3>
                      <p className="text-muted-foreground">Verdient uw investering vaak al binnen enkele jaren terug.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Home className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Waardevermeerdering</h3>
                      <p className="text-muted-foreground">Een woning met zonnepanelen heeft een beter energielabel en is aantrekkelijker voor kopers.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Sun className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Onafhankelijkheid</h3>
                      <p className="text-muted-foreground">Word minder afhankelijk van de schommelende prijzen van energieleveranciers.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Design Technology Section */}
              <div className="mb-12">
                <div className="grid lg:grid-cols-2 gap-8 items-center mb-8">
                  <div>
                    <h2 className="text-3xl font-bold mb-4">Precisie in elk Detail</h2>
                    <p className="text-lg text-muted-foreground mb-4">
                      Met geavanceerde ontwerpsoftware analyseren wij uw dak tot op de centimeter nauwkeurig. 
                      Hierdoor kunnen wij het optimale ontwerp maken voor maximale opbrengst.
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                        <span>Satellietbeelden en luchtfoto-analyse</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                        <span>3D-visualisatie van uw dak</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                        <span>Schaduwanalyse gedurende het jaar</span>
                      </li>
                    </ul>
                  </div>
                  <div className="relative">
                    <img 
                      src="/lovable-uploads/256302e4-63c2-4966-8f66-00b153e5f5c7.png" 
                      alt="3D zonnepaneel ontwerp software"
                      className="rounded-lg shadow-lg w-full"
                    />
                  </div>
                </div>
              </div>

              {/* Process */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold mb-8">Onze Werkwijze: Van Advies tot Installatie</h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
                      1
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Gratis Advies</h3>
                      <p className="text-muted-foreground">We beginnen met een vrijblijvend adviesgesprek waarin we uw wensen, verbruik en de mogelijkheden van uw dak analyseren.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
                      2
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Offerte op Maat</h3>
                      <p className="text-muted-foreground">U ontvangt een heldere offerte zonder verrassingen, inclusief een legplan en een rendementsberekening.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
                      3
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Vakkundige Installatie</h3>
                      <p className="text-muted-foreground">Onze gecertificeerde monteurs zorgen voor een veilige en professionele installatie.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
                      4
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Service & Monitoring</h3>
                      <p className="text-muted-foreground">Na de installatie monitoren we uw systeem en staan we klaar voor al uw vragen.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Affiliate Disclaimer */}
              <div className="mb-8">
                <div className="bg-muted/50 border-l-4 border-primary p-4 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="h-5 w-5 text-primary flex-shrink-0 mt-0.5">ℹ️</div>
                    <div>
                      <h4 className="font-semibold text-sm mb-1">Transparantie in Partnerships</h4>
                      <p className="text-sm text-muted-foreground">
                        SOL-VI werkt samen met partner platforms voor affiliate marketing. Als u via onze links een aankoop doet, 
                        kunnen wij een commissie ontvangen. Dit heeft geen invloed op uw prijs of onze onafhankelijke productadviezen. 
                        Alle producten die wij aanbevelen worden geleverd door erkende leveranciers, niet door SOL-VI zelf.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Final CTA */}
              <div className="text-center bg-muted/50 rounded-lg p-8">
                <h2 className="text-2xl font-bold mb-4">Klaar om uw eigen stroom op te wekken?</h2>
                <p className="text-muted-foreground mb-6">Zet vandaag de stap naar een lagere energierekening.</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button variant="hero" size="lg" onClick={() => (window as any).renderOpenSolarWidget()}>
                    Vraag een vrijblijvende offerte aan
                  </Button>
                  <Button variant="outline" size="lg" onClick={() => (window as any).Calendly.initPopupWidget({url: 'https://calendly.com/sol-vi/new-meeting?primary_color=ff7800', pageSettings: {backgroundColor: 'ffffff', hideEventTypeDetails: false, hideLandingPageDetails: false, primaryColor: 'ff7800', textColor: '4d5055'}, prefill: {}, utm: {}})}>
                    Plan uw gratis adviesgesprek
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* FAQ Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-8">Veelgestelde Vragen over Zonnepanelen</h2>
              <div className="space-y-6">
                {faqData.map((faq, index) => (
                  <div key={index} className="bg-background rounded-lg p-6 shadow-sm">
                    <h3 className="text-lg font-semibold mb-3">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <RelatedServicesSection currentService="zonnepanelen" />
        <CTASection />
      </main>
      <Footer />
    </div>
  );
};

export default Zonnepanelen;